import { Divider, Tab, Tabs, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import React from 'react';
import { Helmet } from 'react-helmet';
import { FormattedMessage, useIntl } from 'react-intl';

import { BackToCompanySettings } from 'components/BackToCompanySettings';
import GlobalNav from 'components/GlobalNav';
import { useQueryState } from 'hooks/useQueryState';

import CustomTerminology from './CustomTerminology';
import MergeKeys from './MergeKeys';
import { messages } from './messages';

// ********************************************************************************
// == Constant ====================================================================
const useStyles = makeStyles(theme => ({
  container: {
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'row',
    marginLeft: 'auto',
    marginRight: 'auto',
    height: 'calc(100% - 12px)',
    gap: theme.spacing(10),
    maxWidth: '808px',
    padding: theme.spacing(0, 10),
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(0, 3),
    },
  },
  divider: {
    margin: 0,
  },
  header: {
    marginTop: theme.spacing(2),
  },
  headerText: {
    lineHeight: '32px',
    fontWeight: 500,
  },
  settingsContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  subtitle: {
    color: theme.palette.common.gray700,
    marginTop: theme.spacing(1),
  },
  tabs: {
    marginTop: theme.spacing(3),
  },
}));

// == Component ===================================================================
const TranslationSettings = () => {
  const classes = useStyles();
  const intl = useIntl();

  // -- State ---------------------------------------------------------------------
  const [tabIndex, setTabIndex] = useQueryState('tabIndex', 0);

  // -- Handler -------------------------------------------------------------------
  const handleTabChange = (
    _event: React.ChangeEvent<unknown>,
    nextTabIndex: number,
  ) => {
    setTabIndex(nextTabIndex);
  };

  // -- UI ------------------------------------------------------------------------
  const activeTab = parseInt(String(tabIndex), 10) || 0;
  return (
    <>
      <Helmet>
        <title>{intl.formatMessage(messages.header)}</title>
        <meta content="Translation Settings" name="description" />
      </Helmet>
      <GlobalNav />
      <div className={classes.container}>
        <div className={classes.settingsContainer}>
          <div className={classes.header}>
            <BackToCompanySettings />
            <Typography
              className={classes.headerText}
              display="inline"
              variant="h2"
            >
              <FormattedMessage {...messages.header} />
            </Typography>
            <Typography className={classes.subtitle} variant="body1">
              <FormattedMessage {...messages.subtitle} />
            </Typography>

            <Tabs
              aria-label={intl.formatMessage(messages.header)}
              className={classes.tabs}
              indicatorColor="primary"
              onChange={handleTabChange}
              textColor="primary"
              value={activeTab}
              variant="standard"
            >
              <Tab
                aria-controls="translation-settings-tabpanel-0"
                id="translation-settings-tab-0"
                label={intl.formatMessage(messages.customTerminologyTab)}
              />
              <Tab
                aria-controls="translation-settings-tabpanel-1"
                id="translation-settings-tab-1"
                label={intl.formatMessage(messages.mergeKeyTab)}
              />
            </Tabs>
            <Divider className={classes.divider} />

            {activeTab === 0 && (
              <div
                aria-labelledby="translation-settings-tab-0"
                id="translation-settings-tabpanel-0"
                role="tabpanel"
              >
                <CustomTerminology />
              </div>
            )}
            {activeTab === 1 && (
              <div
                aria-labelledby="translation-settings-tab-1"
                id="translation-settings-tabpanel-1"
                role="tabpanel"
              >
                <MergeKeys />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default TranslationSettings;
